"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { useSearchParams } from "next/navigation"

import { setAddressBarUrl } from "@/lib/address-bar"
import {
  CONVERSATION_PARAM,
  readInboxSelection,
  writeInboxSelection,
} from "@/lib/conversation/inbox-url"

interface UseInboxSelectionOptions {
  /** Keys older links carry; see `readInboxSelection`. */
  legacyKeys?: readonly string[]
  /**
   * A legacy value as a scoped id — on the spot where only the prefix is missing,
   * after a lookup where the thread is not known yet. `null` is "nothing this
   * surface can open".
   */
  resolveLegacy?: (key: string, value: string) => string | null | Promise<string | null>
}

export interface InboxSelectionState {
  selectedId: string | null
  /** A link that resolved to nothing. Cleared by the next `select`. */
  error: string | null
  select: (id: string | null) => void
}

/**
 * The selected conversation, read from the URL once and written back on every
 * change — `replaceState`, so the in-app Back control closes a pane and the
 * browser's does not.
 *
 * Latest intent wins. A legacy resolve may still be in flight when the operator
 * presses Back or opens another row; a result that lands under an older
 * generation changes nothing — not the selection, not the error, not the URL.
 * A legacy-only URL is rewritten only once its resolve succeeds: clearing the key
 * before that would destroy the one thing a retry needs.
 */
export function useInboxSelection(options: UseInboxSelectionOptions = {}): InboxSelectionState {
  const latest = useRef(options)
  latest.current = options
  // Seeded on the first render so a deep link or a reload opens on the pane,
  // not on a list that is replaced a frame later — and so nothing gated on
  // "nothing selected" fires a request for that one frame. Null without a
  // router (tests); the effect below covers that mount.
  const params = useSearchParams()
  const [selectedId, setSelectedId] = useState<string | null>(
    () => params?.get(CONVERSATION_PARAM) ?? null,
  )
  const [error, setError] = useState<string | null>(null)
  const generation = useRef(0)

  const select = useCallback((id: string | null) => {
    generation.current += 1
    setError(null)
    setSelectedId(id)
    writeUrl(id, latest.current.legacyKeys)
  }, [])

  useEffect(() => {
    const { legacyKeys = [], resolveLegacy } = latest.current
    const params = new URL(window.location.href).searchParams
    const found = readInboxSelection(params, { legacyKeys })
    if (!found) return

    if (found.kind === "scoped") {
      setSelectedId(found.id)
      // A legacy key left beside the canonical one is dropped now.
      if (legacyKeys.some((key) => params.has(key))) writeUrl(found.id, legacyKeys)
      return
    }
    if (!resolveLegacy) return

    const started = ++generation.current
    Promise.resolve()
      .then(() => resolveLegacy(found.key, found.value))
      .then(
        (id) => {
          if (generation.current !== started) return
          if (!id) {
            setError("This link points at nothing this inbox can open.")
            return
          }
          setSelectedId(id)
          writeUrl(id, legacyKeys)
        },
        (cause: unknown) => {
          if (generation.current !== started) return
          setError(cause instanceof Error ? cause.message : "This link could not be opened.")
        },
      )
    return () => {
      generation.current += 1
    }
  }, [])

  return { selectedId, error, select }
}

function writeUrl(id: string | null, legacyKeys: readonly string[] = []): void {
  setAddressBarUrl(writeInboxSelection(new URL(window.location.href), id, { legacyKeys }))
}
